// @flow strict


import React from 'react';
import { injectIntl } from 'react-intl';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createStructuredSelector } from 'reselect';

// Constants
import { PrimaryButton } from 'components/_ReactUI_V1';

import { SENSITIVITY_OPTIONS } from 'utils/constants';

// Styles
import { SidebarBody, BodySection, FooterSection } from '../styles';
import SensitivityVariableOption from '../SensitivityVariable';

// Components
import { fetchReagentInfoOfDataset } from 'services/Dataset/thunks';
import { selectDatasetReagentInfo, isFetchingDatasetReagentInfo } from 'services/Dataset/selectors';

// Types
import type { IntlType } from 'types';
import type {
    SensitivityOptionConstant,
    SensitivityOptionsAvailability,
} from 'services/Dataset/types';

type Props = {
    intl: IntlType,

    loading: boolean,

    handleGenerateDiagramClicked: (
        sensitivityOptionConstant: SensitivityOptionConstant,
        startX: ?number,
        finishX: ?number
    ) => void,
    sensitivityOptionAvailability: SensitivityOptionsAvailability,


    /**
     * The reagent limits for the dataset's reagent
     * Only fetched when the x/y variable is set to reagent concentration
     */
    datasetId: number,
    reagentInfo: ?ReagentInfoForDataset,
    isFetchingReagentInfo: boolean,
    fetchReagentInfoOfDataset: (id: number) => void,
};

type State = {
    selectedVariableX: ?SensitivityOptionConstant,
    startX: ?number,
    finishX: ?number,
};

/**
 * The sidebar body section for the 2D sensitivity plot.
 */
class TwoDSensitivitySidebarSection extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            selectedVariableX: null,
            startX: null,
            finishX: null,
        };
    }

    componentDidMount() {
        this.checkAndFetchReagentInfoForDataset();
    }

    componentDidUpdate(prevProps: Props) {
        if(prevProps.datasetId !== this.props.datasetId) {
            this.checkAndFetchReagentInfoForDataset();
        }
    }

    checkAndFetchReagentInfoForDataset = () => {
        const { datasetId, reagentInfo } = this.props;
        if (reagentInfo && reagentInfo.datasetId === datasetId) {
            // We already have the info for this dataset
            return;
        }

        if (this.props.isFetchingReagentInfo) {
            // Already fetching, no need to re-fetch.
            return;
        }

        this.props.fetchReagentInfoOfDataset(datasetId);
    }

    /**
     * Gets the radio button options for the variable to vary
     * depending their availability
     */
    getVariableRadioOptions = () =>
        Object.keys(SENSITIVITY_OPTIONS)
            .filter((optionKey: string) =>
                Object.keys(this.props.sensitivityOptionAvailability).indexOf(optionKey) !== 1
                    ? this.props.sensitivityOptionAvailability[optionKey]
                    : true
            )
            .map((value: string) => ({
                value,
                label: this.props.intl.formatMessage({
                    id: `constants.SensitivityOptions.${value}`,
                }),
            }));

    isRangeOkay = () => {
        if (!this.state.selectedVariableX) {
            return false;
        }

        // Make sure that if the user has specified both, that the startX is less than the finishX.
        if (this.state.startX !== null && this.state.finishX !== null) {
            if (this.state.startX >= this.state.finishX) {
                return false;
            }
        }

        // Let's assume that the SensitivityVariableOption component ensures that the inputs are valid numbers
        return this.state.startX !== null && this.state.finishX !== null;
    };

    /**
     * Gets whether the generate button is enabled.
     */
    isGenerateButtonEnabled = () =>
        !this.props.loading && Boolean(this.state.selectedVariableX) && this.isRangeOkay();

    /**
     * Generate the diagram.
     */
    handleGenerateDiagramClicked = () =>
        this.isGenerateButtonEnabled() &&
        this.props.handleGenerateDiagramClicked(
            this.state.selectedVariableX,
            this.state.startX,
            this.state.finishX
        );

    render() {
        return (
            <SidebarBody>
                <BodySection>
                    <SensitivityVariableOption
                        variableKey="X"
                        mode="2D"
                        sensitivityOptionAvailability={this.props.sensitivityOptionAvailability}
                        {...this.state}
                        onVariableChange={this.setState.bind(this)}
                    />
                </BodySection>
                <FooterSection withMargin>
                    <PrimaryButton
                        text={this.props.intl.formatMessage({
                            id: `components.CircuitComputationSidebar.2DSensitivity.generatePlotButton`,
                        })}
                        loading={this.props.loading}
                        disabled={!this.isGenerateButtonEnabled() || this.props.loading}
                        onClick={this.handleGenerateDiagramClicked}
                    />
                </FooterSection>
            </SidebarBody>
        );
    }
}


const mapStateToProps = () =>
    createStructuredSelector({
        reagentInfo: selectDatasetReagentInfo(),
        isFetchingReagentInfo: isFetchingDatasetReagentInfo(),
    });

const mapDispatchToProps = (dispatch: ReduxDispatch) =>
    bindActionCreators(
        {
            fetchReagentInfoOfDataset,
        },
        dispatch
    );


export default connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(TwoDSensitivitySidebarSection));
